import { useState, useEffect } from 'react'
import { Icons } from '@journ/ui/icons'
import { Link, useLocation } from '@/libs/router'
import { tv } from '@/libs/tv'
import { useNavItems } from './use-nav-items'
import type { NavItem, NavChild } from '../domain/nav'
import type { JSX } from 'react/jsx-runtime'

const navIcons: Record<string, () => JSX.Element> = {
	'/': () => <Icons.Overview size={20} />,
	'/chat': () => <Icons.IMessage size={20} />,
	'/tasks': () => <Icons.Match size={20} />,
	'/invoices': () => <Icons.Invoice size={20} />,
	'/customers': () => <Icons.Customers size={20} />,
	'/vault': () => <Icons.Vault size={20} />,
	'/reports': () => <Icons.Monitoring size={20} />,
	'/hr': () => <Icons.AccountCircle size={20} />,
	'/settings': () => <Icons.Settings size={20} />,
}

const childRow = tv({
	base: 'ml-8.75 mr-3.75 h-8 flex items-center border-l border-e6e6e6 pl-3 transition-all duration-200 ease-out',
	variants: {
		show: {
			true: 'opacity-100 translate-x-0',
			false: 'opacity-0 -translate-x-2',
		},
	},
})

const childLabel = tv({
	base: 'text-xs font-medium transition-colors duration-200 whitespace-nowrap overflow-hidden text-888888 group-hover/child:text-primary',
	variants: {
		active: { true: 'text-primary' },
	},
})

const itemBackground = tv({
	base: 'border border-transparent h-10 transition-all duration-200 ease-move ml-3.75 mr-3.75',
	variants: {
		active: {
			true: 'bg-f7f7f7 border-e6e6e6',
		},
		expanded: {
			true: 'w-[calc(100%-30px)]',
			false: 'w-10',
		},
	},
})

const itemIcon = tv({
	base: 'absolute top-0 left-3.75 size-10 flex items-center justify-center dark:text-slate-400 text-black group-hover:text-primary! pointer-events-none',
	variants: {
		active: { true: 'dark:text-white!' },
	},
})

const itemLabel = tv({
	base: 'text-sm font-medium transition-opacity duration-200 ease-in-out whitespace-nowrap overflow-hidden text-666666 group-hover:text-primary',
	variants: {
		active: { true: 'text-primary' },
		hasChildren: { true: 'pr-2' },
	},
})

const chevronButton = tv({
	base: 'w-8 h-8 flex items-center justify-center transition-all duration-200 ml-auto mr-3 text-888888 hover:text-primary pointer-events-auto',
	variants: {
		active: { true: 'text-primary/60' },
		open: { true: 'rotate-180' },
	},
})

const childrenContainer = tv({
	base: 'transition-all duration-300 ease-out overflow-hidden',
	variants: {
		open: {
			true: 'max-h-96 mt-1',
			false: 'max-h-0',
		},
	},
})

type ChildItemProps = {
	child: NavChild
	isActive: boolean
	isExpanded: boolean
	shouldShow: boolean
	index: number
	onSelect?: () => void
}

const ChildItem = ({ child, isActive, shouldShow, isExpanded, index, onSelect }: ChildItemProps) => {
	const show = isExpanded && shouldShow

	return (
		<Link to={child.path} onClick={onSelect} className='block group/child'>
			<div
				className={childRow({ show })}
				style={{ transitionDelay: show ? `${40 + index * 20}ms` : `${index * 20}ms` }}
			>
				<span className={childLabel({ active: isActive })}>{child.name}</span>
			</div>
		</Link>
	)
}

type ItemProps = {
	item: NavItem
	isActive: boolean
	isExpanded: boolean
	isItemExpanded: boolean
	onToggle: (path: string) => void
	onSelect?: () => void
}

const Item = ({ item, isActive, isExpanded, isItemExpanded, onToggle, onSelect }: ItemProps) => {
	const Icon = navIcons[item.path]
	const location = useLocation()
	const hasChildren = Boolean(item.children?.length)
	const shouldShowChildren = isExpanded && isItemExpanded

	const handleChevronClick = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		onToggle(item.path)
	}

	return (
		<div className='group'>
			<Link to={item.path} onClick={onSelect}>
				<div className='relative'>
					<div className={itemBackground({ active: isActive, expanded: isExpanded })} />
					<div className={itemIcon({ active: isActive })}>{Icon && <Icon />}</div>
					{isExpanded && (
						<div className='absolute top-0 left-13.75 right-1 h-10 flex items-center pointer-events-none'>
							<span className={itemLabel({ active: isActive, hasChildren })}>{item.name}</span>
							{hasChildren && (
								<button
									type='button'
									onClick={handleChevronClick}
									className={chevronButton({ active: isActive, open: shouldShowChildren })}
								>
									<Icons.ChevronDown size={16} />
								</button>
							)}
						</div>
					)}
				</div>
			</Link>
			{hasChildren && (
				<div className={childrenContainer({ open: shouldShowChildren })}>
					{item.children!.map((child, index) => (
						<ChildItem
							key={child.path}
							child={child}
							isActive={location.pathname === child.path}
							isExpanded={isExpanded}
							shouldShow={shouldShowChildren}
							index={index}
							onSelect={onSelect}
						/>
					))}
				</div>
			)}
		</div>
	)
}

type Props = {
	isExpanded?: boolean
	onSelect?: () => void
}

const MainMenu = ({ isExpanded = false, onSelect }: Props) => {
	const location = useLocation()
	const part = location.pathname.split('/')[1]
	const [expandedItem, setExpandedItem] = useState<string | null>(null)
	const navItems = useNavItems()

	useEffect(() => {
		setExpandedItem(null)
	}, [isExpanded])

	return (
		<div className='mt-4 w-full'>
			<nav className='w-full'>
				<div className='flex flex-col gap-2'>
					{navItems.map(item => {
						const isActive =
							(location.pathname === '/' && item.path === '/') ||
							(location.pathname !== '/' && item.path !== '/' && item.path.startsWith(`/${part}`))

						return (
							<Item
								key={item.path}
								item={item}
								isActive={isActive}
								isExpanded={isExpanded}
								isItemExpanded={expandedItem === item.path}
								onToggle={path => setExpandedItem(expandedItem === path ? null : path)}
								onSelect={onSelect}
							/>
						)
					})}
				</div>
			</nav>
		</div>
	)
}

export { MainMenu }
